import { useEffect, useState } from "react";

import { toPhone } from "common/utils/formatting";
import { parseNumber } from "common/utils/numbers";
import Spinner from "common/components/Spinner";

import Modal from "client/common/components/deprecated-modals/Modal";
import VerificationInput from "client/common/components/inputs/VerificationInput";
import Alert from "client/common/components/content/Alert";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import TextButton from "client/common/components/buttons/TextButton";

import { useSendVerificationCodeMutation, useSignUpMutation } from "../../hooks";
import { SignupFormValues } from "../../types";

import styles from "../../styles/VerifySmsSignupModal.module.less";

const CODE_LENGTH = 6;

type VerifySmsSignupModalProps = {
  visible: boolean;
  onClose: () => void;
  next?: string;
  values: SignupFormValues;
  onTryDifferentNumber: () => void;
  onSendToEmail: () => void;
  /** Hide the "try a different number" option when the phone (username) is locked on file. */
  lockUsername: boolean;
  /** Hide the "send to email instead" option when the user is pinned to the SMS channel. */
  canSwitchChannel: boolean;
};

const VerifySmsSignupModal = (props: VerifySmsSignupModalProps) => {
  const {
    visible,
    onClose,
    values,
    next,
    onTryDifferentNumber,
    onSendToEmail,
    lockUsername,
    canSwitchChannel,
  } = props;
  const phoneNumber = parseNumber(values.phone, 0);

  const [code, setCode] = useState("");

  const {
    mutate: sendCode,
    isLoading: isSendingCode,
    isSuccess: hasSentCode,
    error: sendingCodeError,
    reset: resetSmsCode,
  } = useSendVerificationCodeMutation();
  const {
    mutate: signUp,
    isLoading: isSigningUp,
    error: signUpError,
    reset: resetSignup,
  } = useSignUpMutation("phone", next);

  const error = sendingCodeError || signUpError;
  const nonFieldErrors = error?.errors?.non_field_errors;
  const errorDisplay =
    nonFieldErrors && nonFieldErrors.length > 0 ? nonFieldErrors[0] : error?.detail;

  useEffect(() => {
    // Since this component mounts in the background
    // reset values when it becomes visible
    if (visible) {
      resetSignup();
      resetSmsCode();
      setCode("");
    }
  }, [resetSignup, resetSmsCode, visible]);

  const onSubmit = () => {
    signUp({
      number: phoneNumber,
      email: values.email,
      code,
      profile: {
        first_name: values.first_name,
        last_name: values.last_name,
        new_password: values.password,
      },
    });
  };

  return (
    <Modal visible={visible} onClose={onClose}>
      <ModalLayout.Header
        title="We texted you"
        description={
          <>
            Please enter verification code sent to <strong>{toPhone(phoneNumber)}</strong>
          </>
        }
        onClose={onClose}
      />
      <ModalLayout.Form
        canSubmit={code.length === CODE_LENGTH}
        onSubmit={onSubmit}
        isLoading={isSigningUp}
        submitText="Submit"
      >
        {hasSentCode && <Alert variant="success">Code resent to {toPhone(phoneNumber)}</Alert>}
        {errorDisplay && <Alert variant="danger">{errorDisplay}</Alert>}
        <div className={styles.input}>
          <VerificationInput value={code} onChange={setCode} size={CODE_LENGTH} />
        </div>
        {isSendingCode ? (
          <div className={styles.sending}>
            <Spinner />
          </div>
        ) : (
          <div className={styles.resend}>
            <div className={styles.helperText}>
              Didn&apos;t get a text?
              <br />
              Make sure the number can receive SMS messages.
            </div>
            <div className={styles.optionsList}>
              <div className={styles.option}>
                <TextButton
                  type="button"
                  disabled={isSigningUp || isSendingCode}
                  onClick={() => sendCode({ number: phoneNumber })}
                  icon="right"
                  hasUnderline={true}
                >
                  Resend code
                </TextButton>
              </div>
              {!lockUsername && (
                <div className={styles.option}>
                  <TextButton
                    type="button"
                    disabled={isSigningUp || isSendingCode}
                    onClick={() => {
                      onTryDifferentNumber();
                      onClose();
                    }}
                    icon="right"
                    hasUnderline={true}
                  >
                    Try a different number
                  </TextButton>
                </div>
              )}
              {canSwitchChannel && (
                <div className={styles.option}>
                  <TextButton
                    type="button"
                    disabled={isSigningUp || isSendingCode}
                    onClick={() => {
                      onSendToEmail();
                      onClose();
                    }}
                    icon="right"
                    hasUnderline={true}
                  >
                    Send to email instead
                  </TextButton>
                </div>
              )}
            </div>
          </div>
        )}
      </ModalLayout.Form>
    </Modal>
  );
};

export default VerifySmsSignupModal;
